/**
 * Version comparison utilities for What's New feature
 *
 * Handles semantic version comparison in YY.M.patch format.
 */

/**
 * Parse version string into numeric components
 * @param version - Version string in YY.M.patch format (e.g., "26.2.1")
 * @returns Array of [year, month, patch] numbers
 */
function parseVersion(version: string): [number, number, number] {
  const parts = version.split('.').map((p) => parseInt(p, 10))
  return [parts[0] || 0, parts[1] || 0, parts[2] || 0]
}

/**
 * Compare two version strings
 * @param a - First version string
 * @param b - Second version string
 * @returns Positive if a > b, negative if a < b, zero if equal
 *
 * @example
 * compareVersions('26.2.1', '26.1.0') // returns positive (newer)
 * compareVersions('26.1.0', '26.2.1') // returns negative (older)
 * compareVersions('26.1', '26.1.0')   // returns zero (equal)
 */
export function compareVersions(a: string, b: string): number {
  const [aYear, aMonth, aPatch] = parseVersion(a)
  const [bYear, bMonth, bPatch] = parseVersion(b)

  // Compare year first
  if (aYear !== bYear) return aYear - bYear

  // Then month
  if (aMonth !== bMonth) return aMonth - bMonth

  // Finally patch
  return aPatch - bPatch
}

/**
 * Check if there are unseen updates
 * @param currentVersion - Current app version
 * @param lastSeenVersion - Last version user saw (from localStorage)
 * @returns true if current version is newer than last seen
 *
 * @example
 * hasUnseenUpdates('26.2.1', '26.1.0') // true
 * hasUnseenUpdates('26.1.0', '26.2.1') // false
 * hasUnseenUpdates('26.1.0', null)     // true (first time)
 */
export function hasUnseenUpdates(
  currentVersion: string,
  lastSeenVersion: string | null
): boolean {
  if (!lastSeenVersion) return true
  return compareVersions(currentVersion, lastSeenVersion) > 0
}
